/* @ngInject */

export default (
  $q,
  $rootScope,
  dateTimeService,
  helperService,
  helperPdfService,
  incomeService,
  assetFinanceService,
  homeLoanService,
  quickToolService,
  businessDetailsPdfService,
  contactPdfService,
  incomePdfService,
  householdsPdfService,
  assetsPdfService,
  liabilitiesPdfService,
  assetsAndLiabilitiesPdfService,
  assetFinanceAssetToBeFinancedPdfService,
  supportingCommentaryPdfService,
  homeLoanSecuritiesPdfService,
  preferredProductService,
  financialPositionPdfService,
  objectivesPdfService,
  otherAdvisersPdfService,
  abilityToServicePdfService,
  acknowledgementAndDeclarationPdfService,
  signaturesPdfService,
  homeLoanFundingWorksheetPdfService,
  OtherAdviser,
  INSTANCE
) => {
  /**
   * Prepare asset finance application form for AU.
   *
   * @param {Array} contacts
   * @param {Array} contactLabels
   * @param {Array} lenders
   * @param {Object} ticket
   * @param {Object} ticketOwner
   * @param {Object} loan
   * @param {Object} preferredProduct
   * @param {Object} workflow
   * @param {String} date
   *
   * @returns {Object}
   */
  const prepareAssetFinanceApplicationFormAU = ({
    contacts,
    contactLabels,
    lenders,
    ticket,
    ticketOwner,
    loan,
    preferredProduct,
    workflow,
    date
  }) => [
    businessDetailsPdfService.prepareBusinessDetails({
      lenders,
      contacts,
      contactLabels,
      ticket,
      ticketOwner,
      preferredProduct,
      loan,
      workflow,
      date
    }),
    assetFinanceAssetToBeFinancedPdfService.prepareAssetFinanceAssetToBeFinancedDetails({
      assetDetails: loan?.assetDetails || []
    }),
    preferredProductService.preparePreferredProductDetails({
      lenders,
      preferredProduct,
      loan,
      workflow
    }),
    supportingCommentaryPdfService.prepareSupportingCommentaryDetails({ loan, workflow }),
    abilityToServicePdfService.prepareAbilityToServiceDetails({
      contacts,
      contactLabels,
      ticket,
      workflow,
      loan
    }),
    contactPdfService.preparePersonOrCompanyDetails({
      contacts,
      contactLabels,
      ticket,
      loan,
      workflow,
      showEmployment: true,
      showIncome: true,
      pageBreak: 'before'
    }),
    householdsPdfService.prepareHouseholdDetails({
      expenses: ticket?.expenses || [],
      contacts
    }),
    assetsAndLiabilitiesPdfService.prepareAssetsAndLiabilitiesDetails({
      assets: ticket.assets || [],
      liabilities: ticket.liabilities || [],
      contacts,
      homeLoanSecurities: loan?.securityDetails?.securitySplits || [],
      assetCommitmentSchedules: ticket?.assetCommitmentSchedules || [],
      lenders,
      workflow
    }),
    otherAdvisersPdfService.prepareOtherAdvisersDetails({
      otherAdvisers: ticket?.otherAdvisers || [],
      displayingTypes: [
        OtherAdviser.getAvailableOtherAdviserTypes()[0].id,
        OtherAdviser.getAvailableOtherAdviserTypes()[5].id
      ],
      contacts,
      homeLoanSecurities: [],
      insurance: ticket?.insurances || [],
      workflow
    })
  ];

  /**
   * Prepare home loan application form for AU.
   *
   * @param {Array} contacts
   * @param {Array} contactLabels
   * @param {Array} lenders
   * @param {Object} ticket
   * @param {Object} ticketOwner
   * @param {Object} loan
   * @param {Object} preferredProduct
   * @param {Object} workflow
   * @param {String} date
   *
   * @returns {Object}
   */
  const prepareHomeLoanApplicationFormAU = ({
    contacts,
    contactLabels,
    lenders,
    ticket,
    ticketOwner,
    loan,
    preferredProduct,
    workflow,
    date
  }) => [
    businessDetailsPdfService.prepareBusinessDetails({
      lenders,
      contacts,
      contactLabels,
      ticket,
      ticketOwner,
      preferredProduct,
      loan,
      workflow,
      date
    }),
    homeLoanSecuritiesPdfService.prepareHomeLoanSecuritiesDetails({
      homeLoanSecurities: loan?.securityDetails || [],
      contacts
    }),
    preferredProductService.preparePreferredProductDetails({
      lenders,
      preferredProduct,
      loan,
      workflow
    }),
    homeLoanFundingWorksheetPdfService.prepareHomeLoanFundingWorksheetForApplicationForm({
      fundingWorksheets: loan?.fundingWorksheets || {},
      homeLoanSecurities: loan?.securityDetails?.securitySplits || []
    }),
    contactPdfService.preparePersonOrCompanyDetails({
      contacts,
      contactLabels,
      ticket,
      loan,
      workflow
    }),
    contactPdfService.prepareContactEmploymentDetails({ contacts }),
    incomePdfService.prepareIncomeDetails({
      contacts,
      income: ticket.income || [],
      assets: ticket.assets || [],
      homeLoanSecurities: loan?.securityDetails?.securitySplits || [],
      workflow
    }),
    householdsPdfService.prepareHouseholdDetails({
      expenses: ticket?.expenses || [],
      contacts
    }),
    assetsPdfService.prepareAssetsDetails({
      assets: ticket.assets || [],
      contacts,
      homeLoanSecurities: loan?.securityDetails?.securitySplits || [],
      lenders,
      workflow
    }),
    liabilitiesPdfService.prepareLiabilitiesDetails({
      liabilities: ticket.liabilities,
      contacts,
      assets: ticket.assets,
      lenders
    }),
    objectivesPdfService.prepareObjectivesDetails({
      objectives: ticket?.objectives,
      contacts,
      workflow
    })
  ];

  /**
   * Prepare home loan application form for AU.
   *
   * @param {Array} contacts
   * @param {Array} contactLabels
   * @param {Array} lenders
   * @param {Object} ticket
   * @param {Object} ticketOwner
   * @param {Object} loan
   * @param {Object} preferredProduct
   * @param {Object} workflow
   * @param {String} date
   *
   * @returns {Object}
   */
  const prepareHomeLoanApplicationFormNZ = ({
    contacts,
    contactLabels,
    lenders,
    ticket,
    ticketOwner,
    loan,
    preferredProduct,
    workflow,
    date
  }) => [
    businessDetailsPdfService.prepareBusinessDetails({
      lenders,
      contacts,
      contactLabels,
      ticket,
      ticketOwner,
      preferredProduct,
      loan,
      workflow,
      date
    }),
    homeLoanSecuritiesPdfService.prepareHomeLoanSecuritiesDetails({
      homeLoanSecurities: loan?.securityDetails || [],
      contacts
    }),
    preferredProductService.preparePreferredProductDetails({
      lenders,
      preferredProduct,
      loan,
      workflow
    }),
    homeLoanFundingWorksheetPdfService.prepareHomeLoanFundingWorksheetForApplicationForm({
      fundingWorksheets: loan?.fundingWorksheets || {},
      homeLoanSecurities: loan?.securityDetails?.securitySplits || []
    }),
    supportingCommentaryPdfService.prepareSupportingCommentaryDetails({ loan, workflow }),
    contactPdfService.preparePersonOrCompanyDetails({
      contacts,
      contactLabels,
      ticket,
      loan,
      workflow
    }),
    contactPdfService.prepareContactEmploymentDetails({ contacts }),
    incomePdfService.prepareIncomeDetails({
      contacts,
      income: ticket.income || [],
      assets: ticket.assets || [],
      homeLoanSecurities: loan?.securityDetails?.securitySplits || [],
      workflow
    }),
    householdsPdfService.prepareHouseholdDetails({
      expenses: ticket?.expenses || [],
      contacts
    }),
    assetsPdfService.prepareAssetsDetails({
      assets: ticket.assets || [],
      contacts,
      homeLoanSecurities: loan?.securityDetails?.securitySplits || [],
      lenders,
      workflow
    }),
    liabilitiesPdfService.prepareLiabilitiesDetails({
      liabilities: ticket.liabilities,
      contacts,
      assets: ticket.assets,
      lenders
    }),
    objectivesPdfService.prepareObjectivesNZDetails({
      objectives: ticket?.objectives
    }),
    otherAdvisersPdfService.prepareOtherAdvisersDetails({
      otherAdvisers: ticket?.otherAdvisers || [],
      displayingTypes: [
        OtherAdviser.getAvailableOtherAdviserTypes()[0].id,
        OtherAdviser.getAvailableOtherAdviserTypes()[1].id
      ],
      contacts,
      homeLoanSecurities: loan?.securityDetails?.securitySplits || [],
      insurance: ticket?.insurances || [],
      workflow
    })
  ];

  /**
   * Prepare application form.
   *
   * @param {Array} contacts
   * @param {Array} contactLabels
   * @param {Array} lenders
   * @param {Object} ticket
   * @param {Object} ticketOwner
   * @param {Object} loan
   * @param {Object} workflow
   *
   * @returns {Object}
   */
  const prepareApplicationForm = ({
    contacts,
    contactLabels,
    lenders,
    ticket,
    ticketOwner,
    loan,
    preferredProduct,
    workflow,
    date
  }) => {
    const content = [];
    if (workflow?.isAssetFinance() && helperService.getOrganizationCountry().cca2 === 'AU') {
      content.push(
        prepareAssetFinanceApplicationFormAU({
          contacts,
          contactLabels,
          lenders,
          ticket,
          ticketOwner,
          loan,
          preferredProduct,
          workflow,
          date
        })
      );
    }
    if (workflow?.isAssetFinance() && helperService.getOrganizationCountry().cca2 === 'NZ') {
      // content.push(prepareAssetFinanceApplicationFormNZ({
      //   contacts,
      //   contactLabels,
      //   lenders,
      //   ticket,
      //   ticketOwner,
      //   loan,
      //   preferredProduct,
      //   workflow
      // }));
    }
    if (workflow?.isHomeLoan() && helperService.getOrganizationCountry().cca2 === 'AU') {
      content.push(
        prepareHomeLoanApplicationFormAU({
          contacts,
          contactLabels,
          lenders,
          ticket,
          ticketOwner,
          loan,
          preferredProduct,
          workflow,
          date
        })
      );
    }
    if (workflow?.isHomeLoan() && helperService.getOrganizationCountry().cca2 === 'NZ') {
      content.push(
        prepareHomeLoanApplicationFormNZ({
          contacts,
          contactLabels,
          lenders,
          ticket,
          ticketOwner,
          loan,
          preferredProduct,
          workflow,
          date
        })
      );
    }
    return content;
  };

  /**
   * Create application form.
   *
   * @param {Array} contacts
   * @param {Array} contactLabels
   * @param {Array} lenders
   * @param {Object} ticket
   * @param {Object} ticketOwner
   * @param {Object} loan
   * @param {Object} workflow
   * @param {Boolean} isView
   *
   * @returns {Object}
   */
  const createApplicationForm = ({
    contacts,
    contactLabels,
    lenders,
    ticket,
    ticketOwner,
    loan,
    workflow,
    isView
  }) => {
    const deferred = $q.defer();
    let preferredProduct;
    if (workflow?.isAssetFinance()) {
      preferredProduct = assetFinanceService.getPreferredProduct(loan?.compareProducts);
    }
    if (workflow?.isHomeLoan()) {
      preferredProduct = homeLoanService.getPreferredProduct(loan?.compareProducts);
    }
    const now = moment.utc().tz($rootScope.CurrentTimeZone()).format('X');
    const date = dateTimeService.getFormattedDate(now);
    const orgLogo = $rootScope.CurrentOrganization?.organization?.logo
      ? $rootScope.CurrentOrganization.organization.logo.replace(
          /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/gi,
          $rootScope.CurrentOrganization.id
        )
      : INSTANCE.url + INSTANCE.logo;
    let docDefinition = {};

    quickToolService.getBase64FromImageUrl({ url: orgLogo }).then((res) => {
      docDefinition = {
        pageSize: 'A4',
        pageMargins: [40, 20, 40, 20],
        content: [
          {
            table: {
              widths: ['*'],
              headerRows: 1,
              body: [
                [
                  {
                    width: 180,
                    height: 30,
                    image: res.data,
                    margin: [5, 3, 0, 3],
                    style: 'tableHeader'
                  }
                ]
              ]
            },
            layout: 'noBorders'
          },
          prepareApplicationForm({
            contacts,
            contactLabels,
            lenders,
            ticket,
            ticketOwner,
            loan,
            preferredProduct,
            workflow,
            date
          })
        ],
        styles: {
          header: {
            fontSize: 16,
            bold: true,
            margin: [0, 0, 0, 0]
          },
          subHeader: {
            fontSize: 11,
            margin: [0, 0, 0, 0]
          },
          subSubHeader: {
            fontSize: 10,
            margin: [0, 2, 0, 0]
          },
          subSubSubHeader: {
            fontSize: 10,
            margin: [5, 0, 5, 0]
          },
          prepared: {
            fontSize: 9,
            alignment: 'left',
            margin: [0, 2, 0, 0]
          },
          marginTop: {
            margin: [0, 7, 0, 0]
          },
          marginBottom: {
            margin: [0, 0, 7, 0]
          },
          tableFontSize: {
            fontSize: 10
          },
          tableHeader: {
            fillColor: '#eee'
          },
          tableCellMargin: {
            margin: [4, 2, 4, 2]
          },
          tableMarginTop: {
            margin: [0, 10, 0, 0]
          },
          tableCellMarginTop: {
            margin: [0, 2, 0, 0]
          },
          tableCellMarginLeftTopBottom: {
            margin: [4, 2, 0, 2]
          },
          tableCellMarginRightTopBottom: {
            margin: [0, 2, 4, 2]
          },
          tableCellMarginTopBottom: {
            margin: [0, 2, 0, 2]
          },
          tableCellLeftAlign: {
            alignment: 'left'
          },
          tableCellRightAlign: {
            alignment: 'right'
          },
          tableCellCenterAlign: {
            alignment: 'center'
          },
          label: { fontSize: 8, color: '#777' },
          value: { fontSize: 10 },
          valueXS: { fontSize: 8 },
          valueXL: { fontSize: 12 },
          lists: { fontSize: 10 },
          noColor: { color: '#fff' }
        }
      };
      pdfMake.tableLayouts = {
        defaultLayout: {
          hLineWidth(i, node) {
            return i === node.table.headerRows ? 1 : 0.5;
          },
          vLineWidth() {
            return 0.5;
          },
          hLineColor(i, node) {
            return i === 0 || i === node.table.body.length ? '#333' : '#aaa';
          },
          vLineColor(i, node) {
            return i === 0 || i === node.table.widths.length ? '#333' : '#aaa';
          },
          paddingLeft() {
            return 0;
          },
          paddingRight() {
            return 0;
          },
          paddingTop() {
            return 0;
          },
          paddingBottom() {
            return 0;
          }
        },
        header: {
          hLineWidth(i, node) {
            return i === node.table.headerRows ? 0 : 2;
          },
          vLineWidth() {
            return 0;
          },
          hLineColor(i, node) {
            return i === node.table.body.length ? '#000' : '#fff';
          },
          paddingLeft() {
            return 0;
          },
          paddingRight() {
            return 0;
          },
          paddingTop() {
            return 0;
          },
          paddingBottom() {
            return 0;
          }
        },
        subHeader: {
          hLineWidth(i, node) {
            return i === node.table.headerRows ? 0 : 0.5;
          },
          vLineWidth() {
            return 0;
          },
          hLineColor(i, node) {
            return i === node.table.body.length ? '#999' : '#fff';
          },
          paddingLeft() {
            return 0;
          },
          paddingRight() {
            return 0;
          },
          paddingTop() {
            return 2;
          },
          paddingBottom() {
            return 2;
          }
        },
        noBorders: {
          hLineWidth() {
            return 0;
          },
          vLineWidth() {
            return 0;
          },
          hLineColor() {
            return 0;
          },
          paddingLeft() {
            return 5;
          },
          paddingRight() {
            return 5;
          },
          paddingTop() {
            return 3;
          },
          paddingBottom() {
            return 3;
          }
        },
        noBordersNoPadding: {
          hLineWidth() {
            return 0;
          },
          vLineWidth() {
            return 0;
          },
          hLineColor() {
            return 0;
          },
          paddingLeft() {
            return 0;
          },
          paddingRight() {
            return 0;
          },
          paddingTop() {
            return 0;
          },
          paddingBottom() {
            return 0;
          }
        }
      };

      const fileName = `${moment.utc().year()}${
        moment.utc().month() + 1
      }${moment.utc().date()} ${
        helperService.getOrganizationCountry().cca2 === 'NZ'
          ? 'Application form.pdf'
          : 'Application summary.pdf'
      }`;
      if (!_.isEmpty(docDefinition)) {
        if (isView) {
          helperPdfService.viewOrSavePdf({ docDefinition, isView: true }).then(() => {
            deferred.resolve();
          });
        } else {
          helperPdfService
            .viewOrSavePdf({
              ticket,
              docDefinition,
              fileName,
              isView: false
            })
            .then(() => {
              deferred.resolve();
            });
        }
      }
    });
    return deferred.promise;
  };

  return {
    createApplicationForm
  };
};
